import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useContext } from 'react';
import { UserContext } from '../contexts/UserContext';
import { useOnboardingQuickstart } from '../contexts/OnboardingQuickstartContext';

const AccountChecklist = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [pedalCount, setPedalCount] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [showCongrats, setShowCongrats] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const { firestoreUser } = useContext(UserContext);
  const { openQuickstart } = useOnboardingQuickstart();

  // Whether the listings query has answered. Nothing renders before it has:
  // the widget used to paint "Add 1+ Pedals (0/1)" the instant the dashboard
  // mounted and hide itself only once the query came back, which on native
  // (one long-poll round trip per read) showed a fully set-up member their
  // own checklist for a second or more on every visit (Jason, 2026-10-20).
  const [loaded, setLoaded] = useState(false);
  const uid = auth.currentUser?.uid || null;
  const checklistComplete = firestoreUser?.checklistComplete === true;

  useEffect(() => {
    if (!uid || !firestoreUser) return undefined;
    if (checklistComplete) {
      setIsHidden(true);
      setLoaded(true);
      return undefined;
    }
    let cancelled = false;
    const fetchData = async () => {
      try {
        const pedalsSnapshot = await getDocs(query(
          collection(db, 'listings'),
          where('ownerId', '==', uid),
          where('active', '==', true)
        ));
        if (cancelled) return;
        setPedalCount(pedalsSnapshot.size);
        setLoaded(true);
      } catch (error) {
        console.error('Error fetching checklist data:', error);
        // Keep the widget hidden rather than show a wrong unticked list.
      }
    };
    fetchData();
    return () => { cancelled = true; };
    // Once per signed-in uid, or when the flag flips. Keyed on the whole user
    // document before, this re-ran the query (and could re-show the widget)
    // on every users/{uid} write: a trade completing, a verification update,
    // the celebration marker.
  }, [uid, checklistComplete, firestoreUser ? true : false]); // eslint-disable-line react-hooks/exhaustive-deps

  // Everything complete on arrival: hide, and remember it on the user doc so
  // the next visit skips the query entirely. The flag was read for a long
  // time but nothing ever wrote it; checklistComplete is not in the
  // publicProfiles mirror subset and is client-writable under the rules.
  useEffect(() => {
    if (!loaded || isHidden || !uid || !firestoreUser || checklistComplete) return;
    const isVerified = firestoreUser.isVerified || false;
    const hasProfile = !!(firestoreUser.username && (firestoreUser.profileComplete || firestoreUser.shippingAddress?.street1));
    if (isVerified && hasProfile && pedalCount >= 1 && !showCongrats) {
      setIsHidden(true);
      updateDoc(doc(db, 'users', uid), { checklistComplete: true }).catch((e) => {
        console.warn('AccountChecklist: could not persist checklistComplete', e?.code || e);
      });
    }
  }, [loaded, isHidden, uid, firestoreUser, checklistComplete, pedalCount, showCongrats]);

  // Watch for completion changes to trigger congrats message
  useEffect(() => {
    const isVerified = firestoreUser?.isVerified || false;
    const hasProfile = !!(firestoreUser?.username && (firestoreUser?.profileComplete || firestoreUser?.shippingAddress?.street1));
    const hasEnoughPedals = pedalCount >= 1;
    const allComplete = isVerified && hasProfile && hasEnoughPedals;

    // If just became complete (but wasn't hidden yet), show congrats (but don't auto-dismiss)
    if (allComplete && !isComplete && !isHidden) {
      setIsComplete(true);
      setShowCongrats(true);
      // User can manually dismiss by clicking the X
    }
  }, [pedalCount, firestoreUser, isComplete, isHidden]);

  // Auto-collapse the checklist when the user scrolls on mobile. The widget is a
  // fixed bottom panel that otherwise stays open over the content; collapsing it
  // on scroll frees the view. Mirrors the scroll-dismiss behavior of the Add Pedal
  // price/condition popovers (capture-phase scroll listener). One-way: it stays
  // collapsed until the user taps the header to reopen.
  useEffect(() => {
    if (isCollapsed) return undefined;
    const handleScroll = () => {
      if (window.innerWidth <= 768) setIsCollapsed(true);
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isCollapsed]);

  if (isHidden || !loaded || !firestoreUser) return null;

  const isVerified = firestoreUser.isVerified || false;
  const hasProfile = !!(firestoreUser.username && (firestoreUser.profileComplete || firestoreUser.shippingAddress?.street1));
  const hasEnoughPedals = pedalCount >= 1;
  const allComplete = isVerified && hasProfile && hasEnoughPedals;

  // Don't show widget if all requirements already met from start
  if (allComplete && !showCongrats) return null;

  return (
    <>
      <style>{`
        @media (min-width: 769px) {
          .account-checklist {
            position: fixed !important;
            bottom: 2rem !important;
            left: 2rem !important;
            right: auto !important;
            max-width: 350px !important;
            z-index: 1001 !important;
          }
        }
        @media (max-width: 768px) {
          .account-checklist {
            position: fixed !important;
            bottom: 60px !important;
            left: 0 !important;
            right: 0 !important;
            max-width: 100% !important;
            z-index: 999 !important;
            border-radius: 12px 12px 0 0 !important;
          }
        }
        
        /* Ensure it sits behind the tab bar */
        @media (max-width: 1024px) {
          .account-checklist {
            bottom: 60px !important;
            z-index: 999 !important;
          }
        }
      `}</style>
      <div className="account-checklist" style={{
        backgroundColor: '#ffffff',
        border: '2px solid #C9A227',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        fontFamily: 'Nunito, sans-serif',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div 
          onClick={() => setIsCollapsed(!isCollapsed)}
          style={{
            backgroundColor: '#C9A227',
            padding: '1rem 1.25rem',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            userSelect: 'none'
          }}
        >
          <h3 style={{
            fontSize: '1.125rem',
            fontWeight: '700',
            color: '#ffffff',
            margin: 0
          }}>
            Account Checklist
          </h3>
          <svg 
            width="16" 
            height="16" 
            viewBox="0 0 16 16" 
            style={{
              transition: 'transform 0.3s ease',
              transform: isCollapsed ? 'rotate(180deg)' : 'rotate(0deg)',
              fill: '#ffffff'
            }}
          >
            <path d="M8 11.5a1 1 0 01-.71-.29l-5-5a1 1 0 111.42-1.42L8 9.09l4.29-4.3a1 1 0 111.42 1.42l-5 5a1 1 0 01-.71.29z" />
          </svg>
        </div>

        {/* Content */}
        {!isCollapsed && (
          <div style={{ padding: '1.25rem' }}>
            {/* Checklist Items */}
            <div style={{ marginBottom: isComplete ? '1rem' : '0' }}>
              <div style={{ marginBottom: '0.75rem', textAlign: 'left' }}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    openQuickstart();
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    fontSize: 'calc(1rem + 2pt)',
                    fontWeight: '600',
                    color: '#5A9ECB',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    fontFamily: 'Nunito, sans-serif',
                    textAlign: 'left',
                    display: 'block'
                  }}
                >
                  Quick Start Guide
                </button>
              </div>

              {/* User Verification */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '0.75rem',
                gap: '0.5rem'
              }}>
                {isVerified ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '18px', height: '18px', borderRadius: '50%', backgroundColor: '#4db6ac', flexShrink: 0 }}>
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </span>
                ) : (
                  <span style={{ display: 'inline-flex', width: '18px', height: '18px', borderRadius: '50%', border: '1.5px solid #d1d5db', flexShrink: 0 }} />
                )}
                <span style={{
                  fontSize: '1rem',
                  color: '#4A5759',
                  textDecoration: isVerified ? 'line-through' : 'none'
                }}>
                  User Verification
                </span>
              </div>

              {/* Set up Profile */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '0.75rem',
                gap: '0.5rem'
              }}>
                {hasProfile ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '18px', height: '18px', borderRadius: '50%', backgroundColor: '#4db6ac', flexShrink: 0 }}>
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </span>
                ) : (
                  <span style={{ display: 'inline-flex', width: '18px', height: '18px', borderRadius: '50%', border: '1.5px solid #d1d5db', flexShrink: 0 }} />
                )}
                <span style={{
                  fontSize: '1rem',
                  color: '#4A5759',
                  textDecoration: hasProfile ? 'line-through' : 'none'
                }}>
                  Set up Profile
                </span>
              </div>

              {/* Add 1+ Pedals to Pedalboard */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                {hasEnoughPedals ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '18px', height: '18px', borderRadius: '50%', backgroundColor: '#4db6ac', flexShrink: 0 }}>
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </span>
                ) : (
                  <span style={{ display: 'inline-flex', width: '18px', height: '18px', borderRadius: '50%', border: '1.5px solid #d1d5db', flexShrink: 0 }} />
                )}
                <span style={{
                  fontSize: '1rem',
                  color: '#4A5759',
                  textDecoration: hasEnoughPedals ? 'line-through' : 'none'
                }}>
                  Add 1+ Pedals to Pedalboard {!hasEnoughPedals && `(${pedalCount}/1)`}
                </span>
              </div>
            </div>

            {/* Completion Message */}
            {showCongrats && (
              <div style={{
                backgroundColor: '#f0fdf4',
                border: '2px solid #86efac',
                borderRadius: '8px',
                padding: '1rem',
                marginTop: '1rem'
              }}>
                <p style={{
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  color: '#16a34a',
                  margin: '0 0 0.75rem 0',
                  textAlign: 'center'
                }}>
                  🎉 Checklist Complete!
                </p>
                <p style={{
                  fontSize: '0.875rem',
                  color: '#16a34a',
                  margin: '0',
                  textAlign: 'center'
                }}>
                  You're all set. List, browse, and make offers!
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default AccountChecklist;

